import type { EnhancedProductFamilyId, EnhancedProductGroupId } from '#api/enhancements/enhancedTmsApiTypes'
import type { ProductFamilyId } from '#api/tmsApiService'
import { type AxleBrand, axleBrands } from '#app/store/slices/configuration/tireSlice'
import type { FamilyProductPath, GroupProductPath } from '#app/types/AppTypes'

export type TireGeneralType = 'any-tires' | 'brand-flexibility' | 'rim-material' | 'rims' | 'wheel-nut-cover' | 'tire-pressure-monitoring'

export type TireBrandType = AxleBrand
export type TirePropertyType =
  | 'dimensions'
  | 'load-index'
  | 'tire-category'
  | 'tire-pattern'
  | 'rolling-resistance'
  | 'rim-type'
  | 'wheel-carrier'
  | 'front-axle'
  | 'continental'

export type Axle = 'beforeDrivingAxle' | 'behindDrivingAxle' | 'frontAxle' | 'rearAxle' | 'spareWheel'

export type TireFamilyType = TireBrandType | TirePropertyType | TireGeneralType

const isBrandType = (x: TireFamilyType): x is TireBrandType => axleBrands.includes(x as AxleBrand)
const isPropertyType = (x: TireFamilyType): x is TirePropertyType => !isBrandType(x)

export const makeAxleCategory = <GroupId, SubgroupId extends string, Name, Entries extends Record<ProductFamilyId, { type: TireFamilyType }>>(
  groupId: GroupId,
  subGroupId: SubgroupId,
  name: Name,
  entries: Entries,
) => {
  const brands = {} as Record<TireBrandType, { type: TireBrandType; id: EnhancedProductFamilyId; path: FamilyProductPath }>
  const properties = {} as Record<TirePropertyType, { type: TirePropertyType; id: EnhancedProductFamilyId; path: FamilyProductPath }>
  for (const [key, value] of Object.entries(entries)) {
    if (isBrandType(value.type)) {
      const id = key as EnhancedProductFamilyId
      brands[value.type] = { type: value.type, id: id, path: `${groupId}/${subGroupId}/${id}` as FamilyProductPath }
      continue
    }
    if (isPropertyType(value.type)) {
      const id = key as EnhancedProductFamilyId
      properties[value.type] = { type: value.type, id: id, path: `${groupId}/${subGroupId}/${id}` as FamilyProductPath }
    }
  }

  return {
    id: subGroupId as EnhancedProductGroupId,
    name,
    path: `${groupId}/${subGroupId}` as GroupProductPath,
    brands,
    properties,
  }
}

const makeGeneralCategory = <GroupId, SubgroupId extends string, Entries extends Record<ProductFamilyId, { type: TireGeneralType }>>(
  groupId: GroupId,
  subGroupId: SubgroupId,
  entries: Entries,
) => {
  const properties = {} as Record<TireGeneralType, { type: TireGeneralType; id: EnhancedProductFamilyId; path: FamilyProductPath }>
  for (const [key, value] of Object.entries(entries)) {
    const id = key as EnhancedProductFamilyId
    properties[value.type] = { type: value.type, id: id, path: `${groupId}/${subGroupId}/${id}` as FamilyProductPath }
  }
  return {
    id: subGroupId as EnhancedProductGroupId,
    name: 'general',
    path: `${groupId}/${subGroupId}` as GroupProductPath,
    properties,
  }
}

export const tireGroupEntries = (() => {
  const tireParentGroupId = '5001-3Group' as unknown as EnhancedProductGroupId

  const tireFrontAxleGroupId = '5001-3-5Group' as unknown as EnhancedProductGroupId

  const generalTireFamilies = {
    '03748': { type: 'any-tires' },
    '05021': { type: 'brand-flexibility' },
    '00498': { type: 'rim-material' },
    '07066': { type: 'rims' },
    '00148': { type: 'wheel-nut-cover' },
    '03549': { type: 'tire-pressure-monitoring' },
  } as const satisfies Record<ProductFamilyId, { type: TireGeneralType }>

  const frontAxleFamilies = {
    '00666': { type: 'dimensions' },
    '01056': { type: 'load-index' },
    '04360': { type: 'tire-category' },
    '04365': { type: 'tire-pattern' },
    '09027': { type: 'rolling-resistance' },
    '10084': { type: 'continental' },
    '10089': { type: 'goodYear' },
    '10101': { type: 'bridgestone' },
    '10114': { type: 'michelin' },
    '10119': { type: 'pirelli' },
    '10124': { type: 'fate' },
    '10129': { type: 'flexible' },
  } as const satisfies Record<ProductFamilyId, { type: TireFamilyType }>

  const rearAxleFamilies = {
    '00668': { type: 'dimensions' },
    '01057': { type: 'load-index' },
    '04361': { type: 'tire-category' },
    '04366': { type: 'tire-pattern' },
    '09028': { type: 'rolling-resistance' },
    '10085': { type: 'continental' },
    '10102': { type: 'bridgestone' },
    '10110': { type: 'goodYear' },
    '10115': { type: 'michelin' },
    '10120': { type: 'pirelli' },
    '10125': { type: 'fate' },
    '10130': { type: 'flexible' },
  } as const satisfies Record<ProductFamilyId, { type: TireFamilyType }>

  const behindDrivingAxleFamilies = {
    '00669': { type: 'dimensions' },
    '01058': { type: 'load-index' },
    '04363': { type: 'tire-category' },
    '04368': { type: 'tire-pattern' },
    '09030': { type: 'rolling-resistance' },
    '10087': { type: 'continental' },
    '10104': { type: 'bridgestone' },
    '10112': { type: 'goodYear' },
    '10117': { type: 'michelin' },
    '10122': { type: 'pirelli' },
    '10127': { type: 'fate' },
    '10132': { type: 'flexible' },
  } as const satisfies Record<ProductFamilyId, { type: TireFamilyType }>

  const frontOfDrivingAxleFamilies = {
    '00667': { type: 'dimensions' },
    '00671': { type: 'load-index' },
    '04362': { type: 'tire-category' },
    '04367': { type: 'tire-pattern' },
    '10086': { type: 'continental' },
    '10103': { type: 'bridgestone' },
    '10111': { type: 'goodYear' },
    '10116': { type: 'michelin' },
    '10121': { type: 'pirelli' },
    '10126': { type: 'fate' },
    '10131': { type: 'flexible' },
  } as const satisfies Record<ProductFamilyId, { type: TireFamilyType }>

  const spareWheelFamilies = {
    '00670': { type: 'dimensions' as const },
    '04364': { type: 'tire-category' as const },
    '05005': { type: 'rim-type' as const },
    '00109': { type: 'wheel-carrier' as const },
    '10088': { type: 'continental' as const },
    '10105': { type: 'bridgestone' as const },
    '10113': { type: 'goodYear' as const },
    '10118': { type: 'michelin' as const },
    '10123': { type: 'pirelli' as const },
    '10128': { type: 'fate' as const },
    '10133': { type: 'flexible' as const },
  } as const satisfies Record<ProductFamilyId, { type: TireFamilyType }>

  const tireGeneralGroupId = '5001-3-6Group' as unknown as EnhancedProductGroupId
  const tireRearAxleGroupId = '5001-3-4Group' as unknown as EnhancedProductGroupId
  const tireBehindDrivingAxleGroupId = '5001-3-3Group' as unknown as EnhancedProductGroupId
  const tireBeforeDrivingAxleGroupId = '5001-3-2Group' as unknown as EnhancedProductGroupId
  const tireSpareWheelGroupId = '5001-3-1Group' as unknown as EnhancedProductGroupId

  return {
    id: tireParentGroupId,
    path: `${tireParentGroupId}` as const as GroupProductPath,
    general: makeGeneralCategory(tireParentGroupId, tireGeneralGroupId, generalTireFamilies),
    axles: {
      frontAxle: makeAxleCategory(tireParentGroupId, tireFrontAxleGroupId, 'front-axle' as const, frontAxleFamilies),
      rearAxle: makeAxleCategory(tireParentGroupId, tireRearAxleGroupId, 'rear-axle' as const, rearAxleFamilies),
      behindDrivingAxle: makeAxleCategory(tireParentGroupId, tireBehindDrivingAxleGroupId, 'behind-driving-axle' as const, behindDrivingAxleFamilies),
      beforeDrivingAxle: makeAxleCategory(
        tireParentGroupId,
        tireBeforeDrivingAxleGroupId,
        'before-driving-axle' as const,
        frontOfDrivingAxleFamilies,
      ),
    },
    spare: makeAxleCategory(tireParentGroupId, tireSpareWheelGroupId, 'spare-wheel' as const, spareWheelFamilies),
  } as const
})()
